import React, { useState, useEffect } from 'react';

interface AnimatedScoreProps {
  score: number;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
}

export const AnimatedScore: React.FC<AnimatedScoreProps> = ({ 
  score, 
  label,
  size = 'md',
  animate = true 
}) => {
  const [displayScore, setDisplayScore] = useState(animate ? 0 : score);

  const sizes = {
    sm: { svg: 'w-24 h-24', radius: 45, strokeWidth: 6, text: 'text-2xl' },
    md: { svg: 'w-32 h-32', radius: 60, strokeWidth: 8, text: 'text-3xl' },
    lg: { svg: 'w-40 h-40', radius: 75, strokeWidth: 10, text: 'text-4xl' }
  };

  const config = sizes[size];
  const circumference = 2 * Math.PI * config.radius;
  const strokeDashoffset = circumference - (displayScore / 100) * circumference;

  const getColor = (s: number) => {
    if (s >= 80) return { stroke: 'stroke-emerald-500', glow: 'drop-shadow-[0_0_8px_rgba(16,185,129,0.6)]' };
    if (s >= 60) return { stroke: 'stroke-blue-500', glow: 'drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]' };
    if (s >= 40) return { stroke: 'stroke-amber-500', glow: 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' };
    return { stroke: 'stroke-red-500', glow: 'drop-shadow-[0_0_8px_rgba(239,68,68,0.6)]' };
  };

  const colors = getColor(displayScore);

  useEffect(() => {
    if (!animate) {
      setDisplayScore(score);
      return;
    }

    let start = 0;
    const duration = 1500;
    const startTime = Date.now();

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(score * easeOutCubic));

      if (progress >= 1) {
        clearInterval(timer);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [score, animate]);

  return (
    <div className="relative inline-flex flex-col items-center group">
      <svg className={`transform -rotate-90 ${config.svg} ${colors.glow} transition-all duration-300`}>
        <circle
          cx="50%"
          cy="50%"
          r={config.radius}
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          fill="transparent"
          className="text-slate-200"
        />
        <circle
          cx="50%"
          cy="50%"
          r={config.radius}
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={`${colors.stroke} transition-all duration-1000 ease-out`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${config.text} font-bold bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent`}>
          {displayScore}
        </span>
        <span className="text-xs font-semibold text-slate-500 mt-1 uppercase tracking-wide">
          {label}
        </span>
      </div>
    </div>
  );
};